/*! ════════════════════════════════════════════════════════════════
 *  © 2026 Sick Aura Inc. All rights reserved.
 *  Author: David Otero — SICK AURA (https://www.sickaura.com)
 *  PROPRIETARY — this source code and the visual designs it renders
 *  are the exclusive property of Sick Aura Inc. No license is granted
 *  to copy, modify, distribute, or create derivative works, in whole
 *  or in part, without prior written permission. See /LICENSE.txt
 *  ════════════════════════════════════════════════════════════════ */
/* ═══════════════════════════════════════════════════════════════════
   AURA BALL — WebGL backend for index.html's physics soccer ball.
   The EXACT ball from aura.html (subdivided spherical panels, glowing
   white pentagons, black hexagons, UnrealBloom) rendered on the reveal
   canvas. Orientation comes from buildSoccerBall's physics matrix each
   frame, so the state machine, telemetry HUD and scope read the same
   motion they always did.
   Exposes: window.createAuraBall(canvas) -> { frame(M, fp) }
   ═══════════════════════════════════════════════════════════════════ */
import * as THREE from 'https://cdn.jsdelivr.net/npm/three@0.160.0/+esm';
import { EffectComposer } from 'https://cdn.jsdelivr.net/npm/three@0.160.0/examples/jsm/postprocessing/EffectComposer.js/+esm';
import { RenderPass } from 'https://cdn.jsdelivr.net/npm/three@0.160.0/examples/jsm/postprocessing/RenderPass.js/+esm';
import { UnrealBloomPass } from 'https://cdn.jsdelivr.net/npm/three@0.160.0/examples/jsm/postprocessing/UnrealBloomPass.js/+esm';
import { ShaderPass } from 'https://cdn.jsdelivr.net/npm/three@0.160.0/examples/jsm/postprocessing/ShaderPass.js/+esm';

window.createAuraBall = function(canvas){

  /* ---- truncated icosahedron: 12 pentagons + 20 hexagons ---- */
  const PHI=(1+Math.sqrt(5))/2;
  const IV=[[-1,PHI,0],[1,PHI,0],[-1,-PHI,0],[1,-PHI,0],[0,-1,PHI],[0,1,PHI],
            [0,-1,-PHI],[0,1,-PHI],[PHI,0,-1],[PHI,0,1],[-PHI,0,-1],[-PHI,0,1]]
           .map(v=>new THREE.Vector3(...v).normalize());
  const IF=[[0,11,5],[0,5,1],[0,1,7],[0,7,10],[0,10,11],[1,5,9],[5,11,4],[11,10,2],
            [10,7,6],[7,1,8],[3,9,4],[3,4,2],[3,2,6],[3,6,8],[3,8,9],[4,9,5],
            [2,4,11],[6,2,10],[8,6,7],[9,8,1]];
  const cut=(a,b,f)=>new THREE.Vector3().lerpVectors(a,b,f).normalize();

  const tiles=[];
  for(const [ia,ib,ic] of IF){
    const a=IV[ia], b=IV[ib], c=IV[ic];
    tiles.push({ pent:false,
      center:new THREE.Vector3().add(a).add(b).add(c).divideScalar(3).normalize(),
      ring:[cut(a,b,1/3),cut(a,b,2/3),cut(b,c,1/3),cut(b,c,2/3),cut(c,a,1/3),cut(c,a,2/3)] });
  }
  const nbrs=Array.from({length:12},()=>new Set());
  for(const [ia,ib,ic] of IF){ nbrs[ia].add(ib).add(ic); nbrs[ib].add(ia).add(ic); nbrs[ic].add(ia).add(ib); }
  for(let i=0;i<12;i++)
    tiles.push({ pent:true, center:IV[i].clone(), ring:[...nbrs[i]].map(j=>cut(IV[i],IV[j],1/3)) });

  for(const tile of tiles){
    const n=tile.center;
    const t1=Math.abs(n.x)<0.9 ? new THREE.Vector3(1,0,0) : new THREE.Vector3(0,1,0);
    const U=new THREE.Vector3().crossVectors(n,t1).normalize();
    const W=new THREE.Vector3().crossVectors(n,U);
    tile.ring.sort((p,q)=> Math.atan2(p.dot(U),p.dot(W)) - Math.atan2(q.dot(U),q.dot(W)));
    const e1=new THREE.Vector3().subVectors(tile.ring[1],tile.ring[0]);
    const e2=new THREE.Vector3().subVectors(tile.ring[2],tile.ring[0]);
    if(new THREE.Vector3().crossVectors(e1,e2).dot(n)<0) tile.ring.reverse();
  }

  /* ---- scene ---- */
  const renderer=new THREE.WebGLRenderer({canvas, antialias:true, alpha:true});
  renderer.toneMapping=THREE.NoToneMapping;               // pure values: white is WHITE
  const scene=new THREE.Scene();
  renderer.setClearColor(0x000000,0); scene.background=null;   // transparent; a final alpha-key pass keeps black pixels fully clear
  const camera=new THREE.PerspectiveCamera(38,1,0.1,50);
  camera.position.set(0,0,5.6);                           // ball ≈ 52% of frame — tiny canvas, glow has margin

  const whiteMat=new THREE.MeshBasicMaterial({color:0xffffff, transparent:true, opacity:0, depthWrite:false});
  const blackMat=new THREE.MeshBasicMaterial({color:0x000000, transparent:true, opacity:0, depthWrite:false});
  const ball=new THREE.Group(); scene.add(ball);
  const SHRINK=0.965;
  function sphereTri(a,b,c,depth,out){
    if(depth===0){ out.push(a.x,a.y,a.z, b.x,b.y,b.z, c.x,c.y,c.z); return; }
    const ab=new THREE.Vector3().addVectors(a,b).normalize();
    const bc=new THREE.Vector3().addVectors(b,c).normalize();
    const ca=new THREE.Vector3().addVectors(c,a).normalize();
    sphereTri(a,ab,ca,depth-1,out); sphereTri(ab,b,bc,depth-1,out);
    sphereTri(ca,bc,c,depth-1,out); sphereTri(ab,bc,ca,depth-1,out);
  }
  for(const tile of tiles){
    const Cc=tile.ring.reduce((s,p)=>s.add(p), new THREE.Vector3()).divideScalar(tile.ring.length).normalize();
    const pos=[];
    const shrunk=tile.ring.map(p=>new THREE.Vector3().lerpVectors(Cc,p,SHRINK).normalize());
    for(let i=0;i<shrunk.length;i++)
      sphereTri(Cc, shrunk[i], shrunk[(i+1)%shrunk.length], 3, pos);
    const g=new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(pos,3));
    g.computeVertexNormals();
    ball.add(new THREE.Mesh(g, tile.pent ? whiteMat : blackMat));
  }

  /* ---- seam wireframe: every panel outline as ordered line segments, so
     the ball can TRACE itself into existence panel by panel (setDrawRange
     grows the visible segment count in real time). Each seam edge is
     subdivided into ARC steps re-normalized onto the unit sphere, so the
     lines are great-circle curves — a perfect sphere, never a polyhedron.
     Panels are kept as separate chunks so each formation can re-order them:
     the trace starts on the panel FACING THE CAMERA and spreads outward
     across neighbours — a wave crossing the sphere. ---- */
  const ARC=8;                                            // sub-segments per seam edge — curvature resolution
  const tileWire=[];                                      // {center, pos[]} per panel
  for(const tile of tiles){
    const Cc=tile.ring.reduce((s,p)=>s.add(p), new THREE.Vector3()).divideScalar(tile.ring.length).normalize();
    const shrunk=tile.ring.map(p=>new THREE.Vector3().lerpVectors(Cc,p,SHRINK).normalize());
    const pos=[];
    for(let i=0;i<shrunk.length;i++){
      const a=shrunk[i], b=shrunk[(i+1)%shrunk.length];
      let prev=a;
      for(let k=1;k<=ARC;k++){
        const q=new THREE.Vector3().lerpVectors(a,b,k/ARC).normalize();  // back onto the sphere
        pos.push(prev.x,prev.y,prev.z, q.x,q.y,q.z);
        prev=q;
      }
    }
    tileWire.push({center:Cc.clone(), pos});
  }
  const wirePos=[];
  for(const t of tileWire) wirePos.push(...t.pos);        // provisional order; re-sorted per formation
  const wireGeo=new THREE.BufferGeometry();
  wireGeo.setAttribute('position', new THREE.Float32BufferAttribute(wirePos,3));
  const tmpV=new THREE.Vector3();
  function orderWireFromFront(q){
    // start = panel most squarely facing the lens; the rest sorted by
    // spherical distance from it, so the trace grows through adjacent panels
    let start=0, best=-2;
    for(let i=0;i<tileWire.length;i++){
      const z=tmpV.copy(tileWire[i].center).applyQuaternion(q).z;
      if(z>best){ best=z; start=i; }
    }
    const S=tileWire[start].center;
    const sorted=tileWire.slice().sort((a,b)=>b.center.dot(S)-a.center.dot(S));
    const arr=wireGeo.attributes.position.array;
    let o=0;
    for(const t of sorted){ arr.set(t.pos,o); o+=t.pos.length; }
    wireGeo.attributes.position.needsUpdate=true;
  }
  const wireMat=new THREE.LineBasicMaterial({color:0xffffff, transparent:true, opacity:0.9});
  const wire=new THREE.LineSegments(wireGeo, wireMat);
  wire.renderOrder=2;
  ball.add(wire);
  const WIRE_VERTS=wirePos.length/3;
  wireGeo.setDrawRange(0,0);
  /* hidden-line removal: an invisible depth-only sphere just under the seams
     occludes the far side, so the trace reads as a SOLID ball being drawn —
     never an x-ray of lines through the shell */
  const occluder=new THREE.Mesh(
    new THREE.SphereGeometry(0.992,64,64),
    new THREE.MeshBasicMaterial({colorWrite:false})
  );
  occluder.renderOrder=1;
  ball.add(occluder);

  const composer=new EffectComposer(renderer);
  composer.addPass(new RenderPass(scene,camera));
  const bloom=new UnrealBloomPass(new THREE.Vector2(1,1), 0.5, 0.25, 0.30);  // soft glow, sharp corners
  bloom.strength=0;                                       // glow arrives in its own act
  composer.addPass(bloom);
  /* alpha-key: bloom's composite lifts alpha across the whole frame, which
     reads as a faint grey square over the page. Re-key alpha from luminance —
     black pixels become fully transparent, light is light. The page behind is
     near-black, so keyed glow composites exactly like additive light. */
  composer.addPass(new ShaderPass({
    uniforms:{ tDiffuse:{value:null} },
    vertexShader:'varying vec2 vUv; void main(){ vUv=uv; gl_Position=projectionMatrix*modelViewMatrix*vec4(position,1.0); }',
    fragmentShader:`uniform sampler2D tDiffuse; varying vec2 vUv;
      void main(){
        vec4 c=texture2D(tDiffuse, vUv);
        float a=clamp(max(c.r,max(c.g,c.b))*1.6, 0.0, 1.0);
        // vignette the alpha: glow fades to NOTHING before the canvas edge,
        // so the frame boundary can never print as a faint square
        float d=distance(vUv, vec2(0.5));
        a*=1.0-smoothstep(0.34, 0.47, d);
        gl_FragColor=vec4(c.rgb, a);
      }`
  }));

  const W=canvas.width||1200, H=canvas.height||1200;
  renderer.setSize(W,H,false); composer.setSize(W,H); bloom.setSize(W,H);
  camera.aspect=W/H; camera.updateProjectionMatrix();

  const m4=new THREE.Matrix4();
  let wireOrdered=false;
  return {
    frame(M, fp){
      // physics matrix is row-major in canvas-2D space (y DOWN); conjugate by
      // diag(1,-1,1) to map into three.js y-UP space: negate the xy/yx/yz/zy terms
      m4.set( M[0],-M[1], M[2],0,
             -M[3], M[4],-M[5],0,
              M[6],-M[7], M[8],0,
                 0,    0,    0,1);
      ball.quaternion.setFromRotationMatrix(m4);
      /* formation in three acts:
         ACT 1 (0–.42)  the seam wireframe traces itself panel by panel
         ACT 2 (.42–.72) panels fill — pentagons white, hexagons black
         ACT 3 (.72–1)  the bloom arrives: white pentagons start to GLOW
         then buildSoccerBall's physics takes over (strikes, rolls, traps). */
      const f=Math.max(0,Math.min(1,fp));
      ball.visible = f>0.001;
      if(f<0.03 && !wireOrdered){ orderWireFromFront(ball.quaternion); wireOrdered=true; }
      if(f>=1) wireOrdered=false;                         // next formation re-picks its front panel
      const tr=Math.min(1, f/0.42);                       // trace progress
      const te=1-Math.pow(1-tr,2.2);                      // ease-out: fast start, settling finish
      wireGeo.setDrawRange(0, 2*Math.floor(WIRE_VERTS/2*te));
      const ff=Math.max(0,Math.min(1,(f-0.42)/0.30));     // fill progress
      whiteMat.opacity = ff;
      blackMat.opacity = ff;
      whiteMat.depthWrite = blackMat.depthWrite = ff>0.5; // solid ball starts hiding far-side seams
      const gg=Math.max(0,Math.min(1,(f-0.72)/0.28));     // final act
      bloom.strength = 0.5*gg;                            // the glow arrives...
      wireMat.opacity = 0.9*(1-gg);                       // ...as the wireframe fades OUT completely
      wire.visible = gg<1;                                // gone before the kicking starts
      const sc=0.92+0.08*f;
      ball.scale.set(sc,sc,sc);
      composer.render();
    }
  };
};
